




























































































import { formatMetricName } from '@/features/core/util/metric-formatters';
import { Column } from '@/features/ui/table/model';
import { EditSpotInput, SpotRole } from '@/types/iot-portal';
import { StringProp } from '@/util/prop-decorators';
import { Component, Vue } from 'vue-property-decorator';
import editSpotMutation from './edit-spot.gql';
import query from './spots.gql';
import {
  AppManagerEditSpotPanelControlEditMutation,
  AppManagerEditSpotPanelControlEditMutation_editSpot_spot_activeDeviceMount_attributes,
  AppManagerEditSpotPanelControlEditMutationVariables,
} from './__generated__/AppManagerEditSpotPanelControlEditMutation';
import {
  AppManagerEditSpotPanelControlQuery,
  AppManagerEditSpotPanelControlQueryVariables,
} from './__generated__/AppManagerEditSpotPanelControlQuery';
import { getPufferspeicherPairLabelMap, Option, labelSets, shortLabelSets } from './editSpotConfig';
import { Action, RootAction } from '@/features/core/store';
import { AddToastMessageParams } from '@/features/core/store/toast';

interface FormData {
  input: Pick<EditSpotInput, 'name' | 'roomName' | 'notes'>;
  componentNumber: string;
  metricConfigurations: Record<string, Omit<EditSpotInput['metricConfigurations'][number], 'name'>>;
  infoAttribute: string;
}

@Component({
  apollo: {
    spots: {
      query,
      fetchPolicy: 'cache-and-network',
      variables(this: EditSpotPanelControl): AppManagerEditSpotPanelControlQueryVariables {
        return { spotId: this.spotId };
      },
    },
  },
  data() {
    return { spots: undefined };
  },
})
export default class EditSpotPanelControl extends Vue {
  @StringProp(true)
  private readonly spotId!: string;
  @RootAction
  private readonly ADD_TOAST_MESSAGES!: Action<AddToastMessageParams, void>;

  private readonly spots?: AppManagerEditSpotPanelControlQuery['spots'];
  //This will only used in case of Dual temperature sensor while submitting the form.
  private readonly shortLabels: Record<string, string> = {};

  private readonly DUAL_TEMPERATURE = SpotRole.DUAL_TEMPERATURE;

  private readonly metricColumns: Column[] = [
    { name: 'name', label: 'Name' },
    { name: 'label', label: 'Beschriftung' },
    { name: 'shortLabel', label: 'Kurzbezeichnung' },
    { name: 'position', label: 'Sortierung' },
  ];
  private readonly metricNames = ['temperature1', 'temperature2'];

  private readonly successMessage = 'Bitte prüfen Sie, ob die aktuellen Alarmeinstellungen noch akzeptabel sind.';
  private readonly errorMessage = 'Beim Speichern der Änderungen ist ein Fehler aufgetreten.';

  private onNameDropDownChange(value: string): void {
    if (this.initialData?.input?.name !== value) {
      this.clearShortLabels();
    }
  }

  // Remove trailing numbers and spaces, e.g., 'Kessel 1' -> 'Kessel'
  private getComponentBaseName(name: string): string {
    if (!name) return '';
    return name.replace(/\s*\d+$/, '').trim();
  }

  private get initialAttributes(): AppManagerEditSpotPanelControlEditMutation_editSpot_spot_activeDeviceMount_attributes[] {
    const allAttributes = this.spots?.first?.activeDeviceMount?.attributes || [];
    if (!allAttributes?.some((attr) => attr.name === 'info')) {
      allAttributes?.push({ name: 'info', value: '', __typename: 'DeviceMountAttributes' }); // Add "info" field if missing
    }

    return allAttributes;
  }

  private get initialData(): Partial<FormData> {
    const isDualTemperature = this.spots?.first?.role === this.DUAL_TEMPERATURE;
    const componentName = this.spots?.first?.name || '';
    const metricConfigurations = Object.fromEntries(
      this.spots?.first?.metricConfigurations.map((config) => {
        const label = config?.label ? config.label : '';
        const shortLabel = isDualTemperature ? shortLabelSets[label] || '' : config.shortLabel || '';

        return [
          config.name,
          {
            ...config,
            shortLabel,
          },
        ];
      }) ?? [],
    );
    return {
      input: {
        ...this.spots?.first,
        name: this.getComponentBaseName(componentName),
      },
      metricConfigurations,
      componentNumber: isDualTemperature ? this.extractComponentNumber(componentName) : '',
      infoAttribute: this.initialAttributes?.find((attr) => attr.name === 'info')?.value || '',
    };
  }

  private get metrics(): { name: string; defaultLabel: string }[] {
    return [
      ...new Set([
        ...(this.spots?.first.metrics.map(({ name }) => name) ?? []),
        ...(this.spots?.first.metricConfigurations.map(({ name }) => name) ?? []),
      ]),
    ]
      .map((name) => ({ name, defaultLabel: formatMetricName(name) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  //**Component Name DropDown Section start**
  private getComponentsNameDropDown(): Option[] {
    const options: Option[] = this.getComponentsNameDropDownOptions();
    const data = options.map((option) => option);
    return data;
  }

  private getComponentsNameDropDownOptions(): Option[] {
    const options = Object.keys(labelSets).map((key) => ({
      label: key,
      value: key,
    }));

    return options;
  }
  //**Component Name DropDown Section end**

  //**Metric Labels DropDown Section start**
  private getMetricLabelsDropDown(metricName: string, formData: FormData): Option[] {
    if (formData?.metricConfigurations) {
      const options: Option[] = this.getDynamicMetricLabelDropDownOptions(metricName, formData);
      const data = options.map((option) => option);
      return data;
    }
    return [];
  }

  private getDynamicMetricLabelDropDownOptions(metricName: string, formData: FormData): Option[] {
    const componentBaseName = this.getComponentBaseName(formData?.input?.name);
    const componentNumber = formData?.componentNumber;
    let options: Option[] = [];

    if (componentBaseName && labelSets[componentBaseName]) {
      // Compose new label by prefixing componentBaseName and componentNumber
      options = labelSets[componentBaseName].map((option) => {
        // e.g., "Kessel 1 Vorlauf"
        const label = `${componentBaseName}${componentNumber ? ' ' + componentNumber : ''} ${option.label}`;
        return { label, value: label };
      });
    }
    // Optionally, filter out already selected labels for other metrics
    const otherMetricName = this.metricNames.find((name) => name !== metricName);
    const otherSelectedLabel = otherMetricName ? formData?.metricConfigurations?.[otherMetricName]?.label : '';
    const notAlreadySelected = options.filter((opt) => opt.label !== otherSelectedLabel);
    return (
      this.applyPufferspeicherPairingFilter(componentBaseName, otherSelectedLabel, options, componentNumber) ??
      notAlreadySelected
    );
  }

  private applyPufferspeicherPairingFilter(
    componentBaseName: string,
    otherSelectedLabel: string | null | undefined,
    options: Option[],
    componentNumber: string,
  ): Option[] | undefined {
    if (componentBaseName !== 'Pufferspeicher' || !otherSelectedLabel) return undefined;
    const pairMap = getPufferspeicherPairLabelMap(componentNumber);
    const pairedLabel = pairMap[otherSelectedLabel];
    if (pairedLabel) {
      const pairedOption = options.find((opt) => opt.label === pairedLabel);
      return pairedOption ? [pairedOption] : undefined;
    }
    return undefined;
  }

  private getMetricNames(): string[] {
    const metricConfigurations = this.spots?.first?.metricConfigurations;
    if (metricConfigurations?.length) {
      return metricConfigurations.map((mc) => mc.name);
    }
    return [];
  }

  private isClearMetricLabelDropDown(formData: FormData, metricName: string): boolean {
    const label = formData?.metricConfigurations?.[metricName]?.label;
    if (label) {
      //Check if the label exists in the options
      const metricLabels = this.getMetricLabelsDropDown(metricName, formData);
      return metricLabels.filter((ml) => ml.label === label).length > 0;
    }
    return true;
  }
  //**Metric Labels DropDown Section end**

  //**Metric Short Labels Section start**
  private updateShortLabelFromMetricLabel(metricName: string, newLabel: string): void {
    const normalizedKey = this.normalizeLabelKey(newLabel || '');
    const shortLabel = shortLabelSets[normalizedKey] || '';
    (this.$refs[metricName] as HTMLElement).textContent = shortLabel;
    this.storeDualTemperatureShortLabelForFormSubmit(metricName, shortLabel);
  }

  private storeDualTemperatureShortLabelForFormSubmit(metricName: string, shortLabel: string): void {
    this.shortLabels[metricName] = shortLabel;
  }

  private getShortLabel(formData: FormData, metricName: string): string {
    const label = formData?.metricConfigurations?.[metricName]?.label || '';
    if (label) {
      const normalizeLabelKey = this.normalizeLabelKey(label);
      const shortLabel = shortLabelSets[normalizeLabelKey] || '';
      this.storeDualTemperatureShortLabelForFormSubmit(metricName, shortLabel);
      return shortLabel;
    }
    return label;
  }

  private clearShortLabels(): void {
    this.metricNames.forEach((metricName) => {
      (this.$refs[metricName] as HTMLElement).textContent = '';
      this.storeDualTemperatureShortLabelForFormSubmit(metricName, '');
    });
  }
  //**Metric Short Labels Section end**

  //Remove any number (and surrounding spaces) before the last word e.g. 'Kessel 1 Vorlauf' -> 'Kessel Vorlauf'
  private normalizeLabelKey(label: string): string {
    return label
      .replace(/\s+\d+\s+/, ' ')
      .replace(/\s+\d+$/, '')
      .trim();
  }
  //This is to extract the component number from the component name e.g. 'Kessel 1 Vorlauf' -> '1'
  private extractComponentNumber(componentName: string): string {
    const parts = componentName.trim().split(/\s+/);
    if (parts.length >= 1) {
      const secondLast = parts[parts.length - 1];
      // Return only if it's a number
      if (/^\d+$/.test(secondLast)) {
        return secondLast;
      }
    }
    return '';
  }

  private displayToastMessage(text: string, className: string): void {
    this.ADD_TOAST_MESSAGES({ messages: [{ text, class: className }] });
  }

  private async editSpot({ input, metricConfigurations, infoAttribute, componentNumber }: FormData): Promise<void> {
    try {
      const allAttributes = this.initialAttributes?.map(({ __typename, ...rest }) => rest);

      allAttributes?.filter((attr) => {
        attr.value = attr.name === 'info' ? (infoAttribute ? infoAttribute : '') : attr.value; // update "info" field value
      });
      //Adding short labels for dual temperature sensor
      const updatedMCData = Object.entries(metricConfigurations).map(([name, config]) => {
        if (this.metricNames.includes(name)) {
          return { ...config, name, shortLabel: this.shortLabels[name] };
        } else {
          return { ...config, name };
        }
      });

      //Updating the component name with component number if available
      input.name = `${input.name}${componentNumber ? ' ' + componentNumber : ''}`;

      const result = await this.$apollo.mutate<
        AppManagerEditSpotPanelControlEditMutation,
        AppManagerEditSpotPanelControlEditMutationVariables
      >({
        mutation: editSpotMutation,
        variables: {
          input: {
            ...input,
            activeDeviceMount: this.spots?.first?.activeDeviceMount?.id
              ? {
                  attributes: allAttributes,
                  deviceMountId: this.spots?.first?.activeDeviceMount?.id,
                }
              : null,
            metricConfigurations: updatedMCData,
            order: this.spots?.first.order ?? 0,
            spotId: this.spotId,
          },
        },
      });
      // Only show success if mutation result contains the expected data
      if (result?.data?.editSpot?.spot) {
        this.displayToastMessage(this.successMessage, 'success');
      } else {
        this.displayToastMessage(this.errorMessage, 'error');
      }
    } catch (error) {
      this.displayToastMessage(this.errorMessage, 'error');
    }
  }
}
